import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, UserWithCompany } from 'src/types/common/user';
import { PRODUCER_PHONE_NUMBER } from 'src/Constants';

// Define the UserState type
interface UserState {
  users: Partial<User>[]; // List of Users
  isDataSyncedToBackend: boolean; // Tracks unsaved changes
  currentUser: Partial<User> | null; // Tracks the User being added or edited
  userWithCompany?: UserWithCompany;
  userToken: string;
  producerPhoneNumber?: string; // Optional field for producer phone number
  producerFormId?: string; // Optional field for producer form ID
}
const initialUser = {
  id: null,
  firstName: '',
  lastName: '',
  phone: '',
  owner: false,
  email: '',
  isCompanyOwner: false,
  profileType: '',
  enabled: false,
  producerPhoneNumber: PRODUCER_PHONE_NUMBER, // Optional field for producer phone number
} as User;

// Initial state
const initialState: UserState = {
  users: [], // Start with an empty array
  isDataSyncedToBackend: true, // No unsaved changes
  currentUser: initialUser, // No current User initially
  userWithCompany: undefined,
  userToken: '',
};

// Create the slice
export const UserSlice = createSlice({
  name: 'Users',
  initialState,
  reducers: {
    // Set User array from backend data
    setAllUser(state, action: PayloadAction<Partial<User>[]>) {
      state.users = action.payload;
      state.isDataSyncedToBackend = true; // No unsaved changes
    },

    // Add a new User to the User array
    setUser(state, action: PayloadAction<Partial<User>>) {
      if (state.users === null || state.users === undefined) {
        state.users = [];
      }
      // Add the new User to the User array
      state.users.push(action.payload);
      state.isDataSyncedToBackend = true; // Mark as dirty due to the new addition
    },

    // Set the currentUser when adding or editing a User
    setCurrentUser(state, action: PayloadAction<Partial<User>>) {
      state.currentUser = action.payload; // Track current User for add/edit
    },

    // Update a specific User by merging its new properties
    updateUser(state, action: PayloadAction<Partial<User> & { id: number }>) {
      const { id, ...updates } = action.payload;
      if (state.users) {
        const index = state.users.findIndex((user) => user.id === id);
        if (index !== -1) {
          state.users[index] = { ...state.users[index], ...updates };
          state.isDataSyncedToBackend = true; // Mark as dirty due to updates
        }
      }
      //state.currentUser = null; // Clear currentUser after updating
    },

    // Mark User as saved
    updateIsSynced(state, action: PayloadAction<boolean>) {
      state.isDataSyncedToBackend = action.payload; // Data is now saved
    },
    setUserToken(state, action: PayloadAction<string>) {
      state.userToken = action.payload;
    },
    setProducerPhoneNumber(state, action: PayloadAction<string>) {
      state.producerPhoneNumber = action.payload; // Set the producer phone number
    },
    setProducerFormId(state, action: PayloadAction<string>) {
      state.producerFormId = action.payload; // Set the producer form ID
    },
    // Clear a specific User by its ID
    deleteUser(state, action: PayloadAction<number>) {
      if (state.users) {
        state.users = state.users.filter((user) => user.id !== action.payload);
        state.isDataSyncedToBackend = true; // Mark as dirty after deletion
      }
      state.currentUser = null; // Clear currentUser after deleting
    },
    setUserWithCompany(state, action: PayloadAction<UserWithCompany>) {
      state.userWithCompany = action.payload;
    },
    //unset user with company
    unsetUserWithCompany(state) {
      state.userWithCompany = undefined;
    },

    // Optionally, clear all User (if needed)
    deleteAllUser(state) {
      state.users = [];
      state.isDataSyncedToBackend = false;
      state.currentUser = null;
    },
  },
});

// Export actions
export const {
  setAllUser,
  updateUser,
  updateIsSynced,
  deleteUser,
  setUser,
  setCurrentUser,
  deleteAllUser,
  setUserToken,
  setProducerPhoneNumber,
  setProducerFormId,
  setUserWithCompany,
  unsetUserWithCompany,
} = UserSlice.actions;

// Export reducer
export default UserSlice.reducer;
